import { SortingState } from "@tanstack/react-table";

import { DEFAULT_JUNCTION } from "src/base-components/FilterPopover/constants";
import {
  FieldDefinition,
  FilterCondition,
  FilterFormData,
} from "src/base-components/FilterPopover/types";
import { logger } from "src/utils/logger";

const DATE_TYPES = new Set<string>(["date", "datetime"]);
const MULTI_VALUE_OPERATORS = new Set(["one_of", "any_of"]);
const FILTER_KEY_RE = /^filter\[([^\]]+)\]\[([^\]]+)\]$/;

const isMultiValue = (type: string, operator: string): boolean =>
  MULTI_VALUE_OPERATORS.has(operator) ||
  (type === "tags" && operator === "contains");

const buildParamKey = (field: string, operator: string): string =>
  `filter[${field}][${operator}]`;

export const parseParamKey = (
  key: string,
): { fieldKey: string; operator: string } | undefined => {
  const match = FILTER_KEY_RE.exec(key);
  return match ? { fieldKey: match[1], operator: match[2] } : undefined;
};

const parseJsonArray = (value: string): string[] => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const parseDateRange = (
  value: string,
): { from: string; to: string } | undefined => {
  try {
    const parsed = JSON.parse(value) as { from?: string; to?: string };
    return parsed.from && parsed.to
      ? { from: parsed.from, to: parsed.to }
      : undefined;
  } catch (error) {
    logger.warn("Failed to parse date range filter from URL", {
      value,
      error,
    });
    return undefined;
  }
};

export const filterFormDataToUrlParams = (
  formData: FilterFormData,
  fields: FieldDefinition[],
): Record<string, string> => {
  const fieldMap = new Map(fields.map((f) => [f.key, f]));
  const group = formData.filter_condition.conditions[0];

  return Object.fromEntries(
    (group?.conditions ?? []).flatMap(({ field, operator, value }) => {
      const def = fieldMap.get(field);
      if (!def || !value) return [];

      if (DATE_TYPES.has(def.type) && operator === "eq") {
        const range = parseDateRange(value);
        return range
          ? [
              [buildParamKey(field, "gte"), range.from],
              [buildParamKey(field, "lte"), range.to],
            ]
          : [];
      }

      if (isMultiValue(def.type, operator)) {
        const items = parseJsonArray(value);
        return items.length === 0
          ? []
          : [[buildParamKey(field, operator), items.join(",")]];
      }

      return [[buildParamKey(field, operator), value]];
    }),
  );
};

export const urlParamsToFilterFormData = (
  params: Record<string, string>,
  fields: FieldDefinition[],
): FilterFormData | undefined => {
  const fieldMap = new Map(fields.map((f) => [f.key, f]));

  const parsed = Object.entries(params).flatMap(([key, value]) => {
    const p = parseParamKey(key);
    if (!p) return [];
    const def = fieldMap.get(p.fieldKey);
    return def ? [{ ...p, value, def }] : [];
  });

  if (parsed.length === 0) return undefined;

  const isDateRangePart = (p: (typeof parsed)[number]): boolean =>
    DATE_TYPES.has(p.def.type) &&
    (p.operator === "gte" || p.operator === "lte");

  const dateGroups = parsed.filter(isDateRangePart).reduce((map, p) => {
    const existing = map.get(p.fieldKey) ?? {};
    map.set(p.fieldKey, { ...existing, [p.operator]: p.value });
    return map;
  }, new Map<string, { gte?: string; lte?: string }>());

  const dateConditions: FilterCondition[] = Array.from(
    dateGroups.entries(),
  ).map(([field, { gte, lte }]) => ({
    junction: DEFAULT_JUNCTION,
    field,
    operator: "eq",
    value:
      gte && lte ? JSON.stringify({ from: gte, to: lte }) : (gte ?? lte ?? ""),
  }));

  const plainConditions: FilterCondition[] = parsed
    .filter((p) => !isDateRangePart(p))
    .map((p) => ({
      junction: DEFAULT_JUNCTION,
      field: p.fieldKey,
      operator: p.operator,
      value:
        isMultiValue(p.def.type, p.operator) && p.value.length > 0
          ? JSON.stringify(p.value.split(","))
          : p.value,
    }));

  const allConditions = [...plainConditions, ...dateConditions];
  if (allConditions.length === 0) return undefined;

  return {
    filter_condition: {
      conditions: [
        {
          junction: DEFAULT_JUNCTION,
          conditions: allConditions,
        },
      ],
    },
  };
};

export const isPopoverManagedKey = (key: string): boolean =>
  parseParamKey(key) !== undefined;

export type SortColumn = SortingState[number];

export const parseSortParam = (sortParam: string): SortColumn => {
  const desc = sortParam.startsWith("-");
  const columnId = desc ? sortParam.slice(1) : sortParam;
  return { id: columnId, desc };
};

export const sortingStateToSortParam = (
  sorting: SortingState,
): string | null => {
  if (sorting.length === 0) return null;
  const { id, desc } = sorting[0];
  return desc ? `-${id}` : id;
};
